import DungeonConfig from './DungeonConfig.js';
import DungeonTheme from './DungeonTheme.js';
import FaceOcclusion from '../occlusion/FaceOcclusion.js';
import AmbientOcclusion from '../ao/AmbientOcclusion.js';
import Impostor from '../rendering/Impostor.js';

/**
 * DungeonStreamer - Chunk-based load/unload of dungeon geometry around the player.
 *
 * Physics-critical geometry (floors + ceilings) is loaded permanently so characters
 * never fall through the world. Only walls/decorations are streamed by chunk.
 * Uses triplanar material groups for seamless texture tiling.
 */
export default class DungeonStreamer {
  /**
   * @param {Object} options
   * @param {World} options.world
   * @param {NPCManager} options.npcManager
   */
  constructor(options = {}) {
    this.world = options.world;
    this.npcManager = options.npcManager;
    this.lootManager = options.lootManager;

    /** Shared reference to DungeonGenerator's opened chests set */
    this._openedChests = options.openedChests || new Set();

    /** Shared reference to DungeonGenerator's picked-up items set */
    this._pickedUpItems = options.pickedUpItems || new Set();

    /** Current level data */
    this.level = null;

    /** Current theme (for material groups) */
    this.theme = null;

    /** Map<string, Array<Object>> chunkKey → cuboid defs in that chunk */
    this._chunkDefs = new Map();

    /** Map<string, Array<Object>> chunkKey → spawn points in that chunk */
    this._chunkSpawns = new Map();

    /** Set<string> currently loaded chunk keys */
    this._loadedChunks = new Set();

    /** Set<string> chunks that have already been visited (enemies spawned) */
    this._visitedChunks = new Set();

    /** Map<string, Array<Cuboid>> chunkKey → actual cuboid instances in world */
    this._chunkCuboids = new Map();

    /** Map<string, Array<string>> chunkKey → material group IDs for this chunk */
    this._chunkGroups = new Map();

    /** Map<string, Array<string>> chunkKey → NPC IDs spawned in that chunk */
    this._chunkNPCs = new Map();

    /** Set<string> NPC IDs of killed enemies (don't respawn) */
    this._killedNPCs = new Set();

    /** Array<Cuboid> permanently loaded cuboids (floors + ceilings) */
    this._permanentCuboids = [];

    /** Array<string> material group IDs for permanent cuboids */
    this._permanentGroupIds = [];

    /** Map<string, Array<Object>> chunkKey → vegetation impostor defs in that chunk */
    this._chunkVegDefs = new Map();

    /** Map<string, Array<string>> chunkKey → impostor IDs in that chunk */
    this._chunkImpostors = new Map();

    /** Throttle timer */
    this._checkTimer = 0;

    /** Config */
    this.chunkSize = DungeonConfig.CHUNK_SIZE;
    this.loadRadius = DungeonConfig.LOAD_RADIUS;
    this.maxLoadedCuboids = DungeonConfig.MAX_LOADED_CUBOIDS;

    /**
     * If true, NPCs spawned from chunks are marked isNetworkControlled (follower mode).
     * Their AI is disabled and positions are driven by authority's 4Hz broadcasts.
     * All peers spawn NPCs for visual rendering and hit detection.
     */
    this.networkControlNPCs = false;
  }

  /**
   * Assign cuboid defs and spawn points from a level into chunk buckets.
   * Floors and ceilings are loaded permanently for solid physics.
   * @param {DungeonLevel} level
   */
  setLevel(level) {
    this.level = level;
    this.theme = level.themeName ? DungeonTheme.getTheme(level.themeName) : null;
    this._chunkDefs.clear();
    this._chunkSpawns.clear();
    this._chunkVegDefs.clear();
    this._chunkImpostors.clear();
    this._loadedChunks.clear();
    this._visitedChunks.clear();
    this._chunkCuboids.clear();
    this._chunkGroups.clear();
    this._chunkNPCs.clear();
    // Don't clear _killedNPCs — persists across streamer resets within same dungeon session

    // Partition cuboid defs: permanent (physics-critical) vs streamed (visual)
    const permanentDefs = [];
    for (const def of level.cuboidDefs) {
      if (def.role === 'floor') {
        permanentDefs.push(def);
      } else {
        // Bucket streamed defs into chunks
        const key = this._chunkKey(def.x, def.z);
        if (!this._chunkDefs.has(key)) this._chunkDefs.set(key, []);
        this._chunkDefs.get(key).push(def);
      }
    }

    // Load ALL floor cuboids permanently (solid physics everywhere)
    this._loadPermanentCuboids(permanentDefs);

    // Bucket vegetation impostor defs into chunks
    if (level.vegetationDefs) {
      for (const veg of level.vegetationDefs) {
        const key = this._chunkKey(veg.x, veg.z);
        if (!this._chunkVegDefs.has(key)) this._chunkVegDefs.set(key, []);
        this._chunkVegDefs.get(key).push(veg);
      }
    }

    // Bucket spawn points into chunks
    for (const sp of level.spawnPoints) {
      const key = this._chunkKey(sp.x, sp.z);
      if (!this._chunkSpawns.has(key)) this._chunkSpawns.set(key, []);
      this._chunkSpawns.get(key).push(sp);
    }

    // Pre-calculate AO using full level geometry (sees all walls, not just loaded ones)
    if (AmbientOcclusion.enabled) {
      AmbientOcclusion.buildVoxelGridFromDefs(level.cuboidDefs, this.world);
      AmbientOcclusion.precalculateForDefs(level.cuboidDefs);
    }
  }

  /**
   * Load all permanent cuboids (floors) into the world.
   * These are never unloaded, ensuring solid ground collision everywhere.
   * @private
   */
  _loadPermanentCuboids(defs) {
    this._permanentCuboids = [];
    this._permanentGroupIds = [];

    // Batch-add all permanent cuboids
    const cuboids = this.world.addCuboidBatch(defs);
    for (let i = 0; i < cuboids.length; i++) {
      const cuboid = cuboids[i];
      const def = defs[i];
      cuboid._dungeonRole = def.role;
      if (def.collisionHeight) cuboid.collisionHeight = def.collisionHeight;
      this._permanentCuboids.push(cuboid);
    }

    // Apply material groups to permanent floor cuboids
    this._applyPermanentMaterialGroups();
  }

  /**
   * Create material groups for all permanently loaded floor cuboids.
   * @private
   */
  _applyPermanentMaterialGroups() {
    const groupManager = this.world.materialGroupManager;
    if (!groupManager || !this.theme) return;

    const floorFaces = [];
    for (const cuboid of this._permanentCuboids) {
      if (cuboid._dungeonRole === 'floor') {
        floorFaces.push({ cuboidKey: cuboid.getKey(), face: 'top' });
      }
    }

    if (floorFaces.length > 0) {
      const floorGroup = groupManager.createGroup({
        name: 'Floor_permanent',
        material: this.theme.floor.top()
      });
      groupManager.assignFacesToGroup(floorGroup.id, floorFaces);
      groupManager.applyGroupMaterial(floorGroup.id, true);
      this._permanentGroupIds.push(floorGroup.id);
    }
  }

  /**
   * Called every frame. Throttled check of player position.
   * @param {number} deltaTime
   * @param {Character} player
   */
  update(deltaTime, player) {
    if (!this.level || !player) return;

    this._checkTimer += deltaTime;
    if (this._checkTimer < DungeonConfig.STREAM_CHECK_INTERVAL) return;
    this._checkTimer = 0;

    this._updateChunks(player.x, player.z);
  }

  /**
   * Update chunks considering all player positions (multiplayer).
   * Loads chunks near any player on the same level for NPC AI coverage.
   * @param {number} deltaTime
   * @param {Character} localPlayer
   * @param {Map} remotePlayers - PlayerSync remote player map
   * @param {number} myLevel - current level index (for filtering remote players)
   */
  updateMultiplayer(deltaTime, localPlayer, remotePlayers, myLevel) {
    if (!this.level || !localPlayer) return;

    this._checkTimer += deltaTime;
    if (this._checkTimer < DungeonConfig.STREAM_CHECK_INTERVAL) return;
    this._checkTimer = 0;

    const cs = this.chunkSize;
    const desired = new Set();

    // Local player chunks
    this._addDesiredChunks(desired, localPlayer.x, localPlayer.z, cs);

    // Remote player chunks (only those on the same level)
    if (remotePlayers) {
      for (const [, remote] of remotePlayers) {
        if (!remote.character || !remote.targetState) continue;
        // Only include peers on the same dungeon level
        if (remote.targetState.lvl !== undefined && remote.targetState.lvl !== myLevel) continue;
        this._addDesiredChunks(desired, remote.character.x, remote.character.z, cs);
      }
    }

    // Unload chunks not in desired set
    for (const key of this._loadedChunks) {
      if (!desired.has(key)) this._unloadChunk(key);
    }

    // Load new chunks (higher budget for multiplayer)
    const MAX_CHUNKS_PER_UPDATE = 4;
    let loaded = 0;
    for (const key of desired) {
      if (!this._loadedChunks.has(key)) {
        this._loadChunk(key);
        loaded++;
        if (loaded >= MAX_CHUNKS_PER_UPDATE) {
          this._checkTimer = DungeonConfig.STREAM_CHECK_INTERVAL;
          break;
        }
      }
    }

    // Enforce budget
    const playerCX = Math.floor(localPlayer.x / cs);
    const playerCZ = Math.floor(localPlayer.z / cs);
    this._enforceBudget(playerCX, playerCZ);
  }

  /**
   * Add desired chunks around a position.
   * @private
   */
  _addDesiredChunks(desired, px, pz, cs) {
    const cx = Math.floor(px / cs);
    const cz = Math.floor(pz / cs);
    for (let dx = -this.loadRadius; dx <= this.loadRadius; dx++) {
      for (let dz = -this.loadRadius; dz <= this.loadRadius; dz++) {
        desired.add(`${cx + dx},${cz + dz}`);
      }
    }
  }

  /**
   * Pre-populate killed NPCs set (for late joiners).
   * @param {Set<string>|Array<string>} npcIds
   */
  setKilledNPCs(npcIds) {
    if (npcIds instanceof Set) {
      for (const id of npcIds) this._killedNPCs.add(id);
    } else if (Array.isArray(npcIds)) {
      for (const id of npcIds) this._killedNPCs.add(id);
    }
  }

  /**
   * Force an immediate chunk update around a position (e.g., on level load).
   * @param {number} x
   * @param {number} z
   */
  forceUpdate(x, z) {
    this._checkTimer = 0;
    this._updateChunks(x, z);
  }

  /**
   * Load/unload chunks based on player position.
   * @private
   */
  _updateChunks(px, pz) {
    const cs = this.chunkSize;
    const playerCX = Math.floor(px / cs);
    const playerCZ = Math.floor(pz / cs);

    // Determine which chunks should be loaded
    const desired = new Set();
    for (let dx = -this.loadRadius; dx <= this.loadRadius; dx++) {
      for (let dz = -this.loadRadius; dz <= this.loadRadius; dz++) {
        desired.add(`${playerCX + dx},${playerCZ + dz}`);
      }
    }

    // Unload chunks that are no longer desired
    for (const key of this._loadedChunks) {
      if (!desired.has(key)) {
        this._unloadChunk(key);
      }
    }

    // Load new chunks (spread across frames: max 2 chunks per update to avoid lag spikes)
    const MAX_CHUNKS_PER_UPDATE = 2;
    let loaded = 0;
    for (const key of desired) {
      if (!this._loadedChunks.has(key)) {
        this._loadChunk(key);
        loaded++;
        if (loaded >= MAX_CHUNKS_PER_UPDATE) {
          // Force another check next frame to load remaining chunks
          this._checkTimer = DungeonConfig.STREAM_CHECK_INTERVAL;
          break;
        }
      }
    }

    // Enforce cuboid budget: if over budget, unload farthest chunks
    this._enforceBudget(playerCX, playerCZ);
  }

  /**
   * Load a chunk: instantiate cuboids + vegetation impostors, apply material groups, spawn enemies.
   * Only walls, pillars, chests, portals are chunked as cuboids.
   * @private
   */
  _loadChunk(key) {
    const defs = this._chunkDefs.get(key);

    let cuboids = [];
    if (defs && defs.length > 0) {
      // Batch-add all cuboids (defers AO invalidation, face occlusion, collision)
      cuboids = this.world.addCuboidBatch(defs);
      // Apply dungeon roles and special properties
      for (let i = 0; i < cuboids.length; i++) {
        const cuboid = cuboids[i];
        const def = defs[i];
        cuboid._dungeonRole = def.role;
        if (def.collisionHeight) cuboid.collisionHeight = def.collisionHeight;
        if (def.role === 'portal' || def.role === 'back-portal') {
          cuboid.noCollision = true;
        }
      }
    }
    this._chunkCuboids.set(key, cuboids);
    this._loadedChunks.add(key);

    // Apply triplanar material groups for seamless tiling
    this._applyMaterialGroups(key, cuboids);

    // Re-darken chests that were already opened (persists across level transitions).
    // Uses level-prefixed key to avoid cross-level false matches.
    const chestLvl = this.level?.levelIndex ?? 0;
    for (const cuboid of cuboids) {
      if (cuboid._dungeonRole === 'chest' && this._openedChests.has(`L${chestLvl}_${cuboid.getKey()}`)) {
        if (cuboid.element) {
          const faces = cuboid.element.querySelectorAll('.face');
          for (const face of faces) face.style.filter = 'brightness(0.4)';
        }
      }
    }

    // Spawn vegetation impostors
    this._loadChunkVegetation(key);

    // Spawn enemies on first visit (all peers spawn for visual + hit detection)
    if (!this._visitedChunks.has(key)) {
      this._visitedChunks.add(key);
      this._spawnChunkEnemies(key);
    }
  }

  /**
   * Create vegetation impostor sprites for a chunk.
   * Uses pre-computed sort boxes from DungeonGeometry (no runtime wall-clip calculation).
   * Applies density filter based on DungeonConfig.VEGETATION_DENSITY.
   * @private
   */
  _loadChunkVegetation(key) {
    const vegDefs = this._chunkVegDefs.get(key);
    if (!vegDefs || vegDefs.length === 0) return;

    // Skip if vegetation is hidden
    if (this.world._vegetationVisible === false) return;

    const densityThreshold = Math.floor(DungeonConfig.VEGETATION_DENSITY * 256);
    const impostorIds = [];

    for (let i = 0; i < vegDefs.length; i++) {
      const veg = vegDefs[i];

      // Density filter: skip impostors above the density threshold
      if (veg.densityRank !== undefined && veg.densityRank >= densityThreshold) continue;

      const id = `veg_${key}_${i}`;
      const impostor = new Impostor({
        id,
        x: veg.x,
        y: veg.y,
        z: veg.z,
        textureUrl: veg.textureUrl,
        spriteWidth: 64,
        spriteHeight: 64,
        scale: veg.scale,
        flipped: veg.flipped
      });

      // Use pre-computed sort box from DungeonGeometry (already clipped against solid geometry)
      if (veg.sortWidth !== undefined) {
        impostor.sortWidth = veg.sortWidth;
        impostor.sortDepth = veg.sortDepth;
        if (veg.sortCenterX !== undefined) impostor.sortCenterX = veg.sortCenterX;
        if (veg.sortCenterZ !== undefined) impostor.sortCenterZ = veg.sortCenterZ;
      }

      impostor._isVegetation = true;
      impostor._skipSync = true; // Dungeon vegetation is locally generated — never broadcast
      this.world.addImpostor(impostor);
      impostor.sampleAO();
      impostorIds.push(id);
    }
    this._chunkImpostors.set(key, impostorIds);
  }

  /**
   * Reload vegetation for all currently loaded chunks.
   * Called when vegetation density changes at runtime.
   */
  reloadAllVegetation() {
    for (const key of [...this._loadedChunks]) {
      // Remove existing vegetation
      const impostorIds = this._chunkImpostors.get(key);
      if (impostorIds) {
        for (const id of impostorIds) {
          this.world.removeImpostor(id);
        }
        this._chunkImpostors.delete(key);
      }
      // Reload with current density/visibility settings
      this._loadChunkVegetation(key);
    }
  }

  /**
   * Create and apply triplanar material groups for a chunk's cuboids.
   * Groups wall faces for seamless tiling. Floors are handled permanently.
   * @private
   */
  _applyMaterialGroups(key, cuboids) {
    const groupManager = this.world.materialGroupManager;
    if (!groupManager || !this.theme) return;

    const groupIds = [];
    const wallFaces = [];
    const pillarFaces = [];
    const chestFaces = [];

    for (const cuboid of cuboids) {
      const role = cuboid._dungeonRole;
      const cuboidKey = cuboid.getKey();
      const allSides = ['front', 'back', 'left', 'right'];

      if (role === 'wall') {
        for (const f of allSides) wallFaces.push({ cuboidKey, face: f });
        wallFaces.push({ cuboidKey, face: 'top' });
      } else if (role === 'pillar') {
        for (const f of allSides) pillarFaces.push({ cuboidKey, face: f });
        pillarFaces.push({ cuboidKey, face: 'top' });
      } else if (role === 'chest') {
        for (const f of allSides) chestFaces.push({ cuboidKey, face: f });
        chestFaces.push({ cuboidKey, face: 'top' });
      }
    }

    // Create wall group
    if (wallFaces.length > 0) {
      const wallGroup = groupManager.createGroup({
        name: `Wall_${key}`,
        material: this.theme.wall.front()
      });
      groupManager.assignFacesToGroup(wallGroup.id, wallFaces);
      groupManager.applyGroupMaterial(wallGroup.id, true);
      groupIds.push(wallGroup.id);
    }

    // Create pillar group
    if (pillarFaces.length > 0) {
      const pillarGroup = groupManager.createGroup({
        name: `Pillar_${key}`,
        material: this.theme.pillar()
      });
      groupManager.assignFacesToGroup(pillarGroup.id, pillarFaces);
      groupManager.applyGroupMaterial(pillarGroup.id, true);
      groupIds.push(pillarGroup.id);
    }

    // Create chest group
    if (chestFaces.length > 0) {
      const chestGroup = groupManager.createGroup({
        name: `Chest_${key}`,
        material: this.theme.chest()
      });
      groupManager.assignFacesToGroup(chestGroup.id, chestFaces);
      groupManager.applyGroupMaterial(chestGroup.id, true);
      groupIds.push(chestGroup.id);
    }

    if (groupIds.length > 0) {
      this._chunkGroups.set(key, groupIds);
    }
  }

  /**
   * Unload a chunk: remove cuboids, material groups, and despawn NPCs.
   * @private
   */
  _unloadChunk(key) {
    // Remove material groups first (before cuboids are removed)
    const groupIds = this._chunkGroups.get(key);
    if (groupIds) {
      const groupManager = this.world.materialGroupManager;
      if (groupManager) {
        for (const gid of groupIds) {
          groupManager.deleteGroup(gid);
        }
      }
      this._chunkGroups.delete(key);
    }

    // Remove cuboids (batch for efficiency)
    const cuboids = this._chunkCuboids.get(key);
    if (cuboids && cuboids.length > 0) {
      this.world.removeCuboidBatch(cuboids);
      this._chunkCuboids.delete(key);
    }

    // Remove vegetation impostors
    const impostorIds = this._chunkImpostors.get(key);
    if (impostorIds) {
      for (const id of impostorIds) {
        this.world.removeImpostor(id);
      }
      this._chunkImpostors.delete(key);
    }

    // Despawn NPCs (track killed state)
    const npcIds = this._chunkNPCs.get(key);
    if (npcIds) {
      for (const npcId of npcIds) {
        const entry = this.npcManager.npcs.get(npcId);
        if (entry && entry.character.combatStats?.isDead) {
          this._killedNPCs.add(npcId);
        }
        this.npcManager.removeNPC(npcId);
      }
      this._chunkNPCs.delete(key);
    }

    this._loadedChunks.delete(key);
  }

  /**
   * Spawn enemies for a chunk based on its spawn points.
   * Uses spawn-point index for deterministic NPC IDs (identical across all peers).
   * On follower peers (networkControlNPCs=true), spawned NPCs have AI disabled.
   * @private
   */
  async _spawnChunkEnemies(key) {
    const spawns = this._chunkSpawns.get(key);
    if (!spawns) return;

    const npcIds = [];
    for (let i = 0; i < spawns.length; i++) {
      const sp = spawns[i];

      // Include level index in all IDs to prevent cross-level collisions —
      // chunk coordinates can repeat across levels (different layouts, same grid space).
      const lvl = this.level?.levelIndex ?? 0;

      if (sp.type === 'item') {
        // Spawn items (e.g., health potions in shrine rooms)
        // Use deterministic IDs so all peers share the same item references
        // for first-come-first-serve pickup broadcasts.
        if (this.lootManager && sp.data) {
          const qty = sp.data.quantity || 1;
          for (let q = 0; q < qty; q++) {
            const itemNetId = `dungeon_L${lvl}_item_${key}_${i}_${q}`;
            // Skip items already picked up (persists across level transitions)
            if (this._pickedUpItems.has(itemNetId)) continue;
            // Deterministic spread offset for multiple items (no Math.random)
            const ox = qty > 1 ? (q / (qty - 1) - 0.5) * 0.8 : 0;
            this.lootManager.spawnNetworkItem(
              itemNetId, sp.data.itemId,
              sp.x + ox, sp.y, sp.z
            );
          }
        }
        continue;
      }

      if (sp.type === 'friendly') {
        const npcId = `dungeon_L${lvl}_friend_${key}_${i}`;
        try {
          const friendlyConfig = {
            id: npcId,
            characterName: sp.data.characterName,
            x: sp.x,
            y: sp.y,
            z: sp.z,
            behaviorType: 'wander',
            wanderRadius: 2,
            dungeonMode: true
          };
          // Pass through LPC data if present
          if (sp.data.lpcAppearance) {
            friendlyConfig.lpcAppearance = sp.data.lpcAppearance;
            friendlyConfig.lpcEquipment = sp.data.lpcEquipment;
            friendlyConfig.displayName = sp.data.displayName;
          }
          await this.npcManager.spawnNPC(friendlyConfig);
          // On follower peers: disable AI, authority drives position
          if (this.networkControlNPCs) {
            const entry = this.npcManager.npcs.get(npcId);
            if (entry) {
              entry.character.isNetworkControlled = true;
              if (entry.character.pathFollower?.isFollowing) entry.character.pathFollower.stop();
            }
          }
          npcIds.push(npcId);
        } catch (e) {
          // skip silently
        }
        continue;
      }

      if (sp.type !== 'enemy') continue;

      // Deterministic ID: level + chunk key + spawn index (identical across all peers)
      const npcId = `dungeon_L${lvl}_${key}_${i}`;

      // Skip if already killed
      if (this._killedNPCs.has(npcId)) continue;

      try {
        const enemyConfig = {
          id: npcId,
          characterName: sp.data.characterName,
          x: sp.x,
          y: sp.y,
          z: sp.z,
          behaviorType: 'wander',
          wanderRadius: 3,
          dungeonMode: true,
          combatConfig: sp.data.combatConfig
        };
        // Pass through LPC data if present
        if (sp.data.lpcAppearance) {
          enemyConfig.lpcAppearance = sp.data.lpcAppearance;
          enemyConfig.lpcEquipment = sp.data.lpcEquipment;
          enemyConfig.displayName = sp.data.displayName;
        }
        await this.npcManager.spawnNPC(enemyConfig);
        // On follower peers: disable AI, authority drives position via NPCSync
        if (this.networkControlNPCs) {
          const entry = this.npcManager.npcs.get(npcId);
          if (entry) {
            entry.character.isNetworkControlled = true;
            if (entry.character.pathFollower?.isFollowing) entry.character.pathFollower.stop();
          }
        }
        npcIds.push(npcId);
      } catch (e) {
        // Character asset may not be loaded yet, skip silently
      }
    }

    this._chunkNPCs.set(key, npcIds);
  }

  /**
   * Enforce the max loaded cuboids budget by unloading farthest chunks.
   * @private
   */
  _enforceBudget(playerCX, playerCZ) {
    let totalCuboids = 0;
    for (const cuboids of this._chunkCuboids.values()) {
      totalCuboids += cuboids.length;
    }

    if (totalCuboids <= this.maxLoadedCuboids) return;

    // Sort loaded chunks by distance from player (farthest first)
    const sorted = [...this._loadedChunks].map(key => {
      const [cx, cz] = key.split(',').map(Number);
      const dist = (cx - playerCX) ** 2 + (cz - playerCZ) ** 2;
      return { key, dist };
    }).sort((a, b) => b.dist - a.dist);

    // Unload farthest chunks until under budget
    for (const { key } of sorted) {
      if (totalCuboids <= this.maxLoadedCuboids) break;
      const cuboids = this._chunkCuboids.get(key);
      if (cuboids) {
        totalCuboids -= cuboids.length;
      }
      this._unloadChunk(key);
    }
  }

  /**
   * Unload all chunks and permanent cuboids. Full cleanup.
   */
  unloadAll() {
    // Exit dungeon AO precalc mode so normal invalidation resumes
    AmbientOcclusion.exitDungeonMode();

    for (const key of [...this._loadedChunks]) {
      this._unloadChunk(key);
    }

    // Remove permanent cuboids
    for (const cuboid of this._permanentCuboids) {
      this.world.removeCuboid(
        cuboid.x, cuboid.y, cuboid.z,
        cuboid.width, cuboid.height, cuboid.depth
      );
    }
    this._permanentCuboids = [];

    // Remove permanent material groups
    const groupManager = this.world.materialGroupManager;
    if (groupManager) {
      for (const gid of this._permanentGroupIds) {
        groupManager.deleteGroup(gid);
      }
    }
    this._permanentGroupIds = [];

    this._chunkDefs.clear();
    this._chunkSpawns.clear();
    this._chunkVegDefs.clear();
    this._chunkImpostors.clear();
    this._visitedChunks.clear();
    // Don't clear _killedNPCs — persists across level transitions within the same
    // dungeon session so dead enemies stay dead when revisiting previous levels.
    // Cleared explicitly in resetSession() when the dungeon run ends.
  }

  /**
   * Full session reset — clears killed NPCs and picked-up items tracking.
   * Called when the dungeon run ends (cleanup).
   */
  resetSession() {
    this._killedNPCs.clear();
    // _pickedUpItems is a shared reference from DungeonGenerator — cleared there
  }

  /**
   * Mark an NPC as killed (so it doesn't respawn when chunk reloads).
   * @param {string} npcId
   */
  markKilled(npcId) {
    this._killedNPCs.add(npcId);
  }

  /**
   * Get total currently loaded cuboid count (chunks + permanent).
   * @returns {number}
   */
  getLoadedCuboidCount() {
    let count = this._permanentCuboids.length;
    for (const cuboids of this._chunkCuboids.values()) {
      count += cuboids.length;
    }
    return count;
  }

  /**
   * Compute chunk key from world coordinates.
   * @private
   */
  _chunkKey(x, z) {
    const cx = Math.floor(x / this.chunkSize);
    const cz = Math.floor(z / this.chunkSize);
    return `${cx},${cz}`;
  }
}
